import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { HttpService } from '@app/core/services/http.service';
import { InventoryItem } from '@chartering/bunker-lube-fw/state/bunker-lube-fw.types';
import { environment } from '@environments/environment';
import { LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { Bunker, InventorySupplyBody } from '@vessel-reports/port/state/port.types';
import { Detail, Inventory, RobReportBody } from '@vessel-reports/rob/state/rob.types';
import { VesselReport } from '@vessel-reports/state/vessel-reports.types';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RobService {
  public static readonly API: string = `${environment.PROJECT_API}/vessel-report-group/reports`;

  protected readonly httpService: HttpService = inject(HttpService);

  createNewReport(body: RobReportBody): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${RobService.API}`, body);
  }

  getDetailReport(report_id: string): Observable<any> {
    return this.httpService.get<{
      data: any;
    }>(`${RobService.API}/${report_id}`, {});
  }

  updateReport(report_id: string, body: any): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${RobService.API}/${report_id}`, body);
  }

  updateDetailReport(body: any): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${RobService.API}/details_bulk`, body);
  }

  submitReport(report_id: string): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${RobService.API}/submit/${report_id}`, {});
  }

  deleteReport(report_id: string): Observable<any> {
    return this.httpService.delete(`${RobService.API}/${report_id}`, {});
  }

  getPreviousReport(vessel_id: string, type?: number): Observable<any> {
    const url = type
      ? `${RobService.API}/previous?filter[vessel_id]=${vessel_id}&filter[report_type_id]=${type}`
      : `${RobService.API}/previous?filter[vessel_id]=${vessel_id}`;
    return this.httpService.get<{
      data: VesselReport;
    }>(url, {});
  }

  getInventories(queryParams?: Params): Observable<any> {
    const defaultParams = {
      page: '1',
      limit: '999'
    };
    return this.httpService.get<{
      data: Inventory;
    }>(`${RobService.API}/inventory_items`, {
      params: new HttpParams({ fromObject: Object.keys(queryParams)?.length ? queryParams : defaultParams })
    });
  }

  getInventoriesSupply(report_id: string): Observable<any> {
    return this.httpService.get<{
      data: VesselReport;
    }>(`${RobService.API}/supplies/${report_id}`, {});
  }

  createInventorySupply(body: InventorySupplyBody): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${RobService.API}/supplies`, body);
  }

  updateInventorySupply(id: string, body: InventorySupplyBody): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${RobService.API}/supplies/${id}`, body);
  }

  deleteInventorySupply(id: string) {
    return this.httpService.delete(`${RobService.API}/supplies/${id}`, {});
  }

  createReportBunker(body: any): Observable<any> {
    return this.httpService.post<{
      data: any;
    }>(`${RobService.API}/bunkers`, body);
  }

  updateReportBunker(report_id: string, body: any): Observable<any> {
    return this.httpService.put<{
      data: any;
    }>(`${RobService.API}/bunkers/${report_id}`, body);
  }

  deleteReportBunker(report_id: string): Observable<any> {
    return this.httpService.delete(`${RobService.API}/bunkers/${report_id}`, {});
  }

  getBunker(bunker_id: string): Observable<any> {
    return this.httpService.get<{
      data: Bunker;
    }>(`${RobService.API}/bunkers/${bunker_id}`, {});
  }

  getAdvanceFilters(key: string, limit?: number, queryParams?: Params): Observable<{ data: any[] }> {
    return this.httpService.get<{ data: any[] }>(`${RobService.API}/advance_filters`, {
      params: {
        advance_filter: key,
        limit: limit ?? LIMIT_PER_PAGE_DEFAULT,
        ...queryParams
      }
    });
  }

  deleteDetail(id: string) {
    return this.httpService.delete(`${RobService.API}/details/${id}`, {});
  }

  updateDetail(id: string, body: Detail) {
    return this.httpService.put(`${RobService.API}/details/${id}`, body);
  }

  createDetail(body: Detail) {
    return this.httpService.post(`${RobService.API}/details`, body);
  }

  updateInventorySequence(body: any) {
    return this.httpService.post(`${RobService.API}/detail_sequences`, body);
  }

  searchInventoryItemListByName(item_name: string) {
    const queryParams = {
      'filter[name]': item_name,
      limit: 999
    };

    return this.httpService.get<{
      data: InventoryItem[];
    }>(`${RobService.API}/inventory_items`, {
      params: queryParams
    });
  }
}
